import React from 'react';
import { Box, Dialog, IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import RuleIcon from '@mui/icons-material/Rule';
import DosenTooltip from '../../../../../../../../components/ui/DosenTooltip';
import { RequirementsView, ViewAudience } from './requirements';
import { closeRequirements } from './useRequirements';
import { AccessText, AudienceTip, BRAND, DerivedNote, FONT, HowTip, LINE, SURFACE, TEXT, TEXT_2, TEXT_3 } from './ui';

const LABEL_COL = 190;
const COL = 170;
const GROUP_EDGE = '1px solid #3A3A3A';
const THICK = '2px solid #4A4A4A';

/** A column: one audience of either group. */
interface Column {
  audience: ViewAudience;
  readOnly: boolean;
  /** The first Read Only column: a stronger line between the two groups. */
  starts: boolean;
}

/**
 * Audience Requirements: every lesson, and what each audience of the course
 * must do with it. Read-only: it all follows from the audience groups.
 */
const RequirementsPanel: React.FC<{ view: RequirementsView | null; loading: boolean; error?: string | null }> = ({
  view,
  loading,
  error = null
}) => {
  const modules = view?.modules ?? [];
  const columns: Column[] = [
    ...(view?.readAssess ?? []).map((audience) => ({ audience, readOnly: false, starts: false })),
    ...(view?.readOnly ?? []).map((audience, i) => ({ audience, readOnly: true, starts: i === 0 }))
  ];

  const gridWidth = LABEL_COL + columns.length * COL;
  const cellSx = (c: Column | 'label', extra: object = {}) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: c === 'label' ? 'flex-start' : 'center',
    minWidth: 0,
    minHeight: 44,
    px: c === 'label' ? 0 : 1,
    pr: c === 'label' ? 1 : undefined,
    borderBottom: `1px solid ${LINE}`,
    borderLeft: c === 'label' ? 'none' : c.starts ? GROUP_EDGE : `1px solid ${LINE}`,
    ...extra
  });

  return (
    <Dialog
      open
      onClose={closeRequirements}
      maxWidth={false}
      PaperProps={{
        sx: {
          background: SURFACE,
          border: `1px solid ${LINE}`,
          borderRadius: '14px',
          width: `min(${gridWidth + 48}px, calc(100vw - 32px))`,
          maxHeight: 'calc(100vh - 64px)',
          display: 'flex',
          flexDirection: 'column'
        }
      }}
    >
      <Box
        sx={{ mx: 3, pt: 2.5, pb: 2, borderBottom: `1px solid ${LINE}`, display: 'flex', alignItems: 'center', gap: 1 }}
      >
        <RuleIcon sx={{ fontSize: 22, color: BRAND }} />
        <Typography sx={{ fontFamily: FONT, fontSize: 18, fontWeight: 600, color: TEXT }}>
          Audience Requirements
        </Typography>
        <DosenTooltip title={<HowTip />}>
          <InfoOutlinedIcon
            aria-label="How audience requirements work"
            sx={{ fontSize: 18, color: TEXT_3, cursor: 'help' }}
          />
        </DosenTooltip>
        <Box sx={{ flex: 1 }} />
        <IconButton aria-label="Close" onClick={closeRequirements} sx={{ color: TEXT_3 }}>
          <CloseIcon sx={{ fontSize: 20 }} />
        </IconButton>
      </Box>

      <Box sx={{ flex: 1, overflow: 'auto', px: 3, pb: 2 }}>
        {loading || error || !view ? (
          <Typography sx={{ fontFamily: FONT, fontSize: 13, color: TEXT_3, pt: 2 }}>
            {error || 'Loading the audiences...'}
          </Typography>
        ) : (
          <>
            {view.derived && <DerivedNote sx={{ pt: 2 }} />}
            {columns.length === 0 && (
              <Typography sx={{ fontFamily: FONT, fontSize: 13, color: TEXT_3, pt: 2 }}>
                This course has no audience groups yet.
              </Typography>
            )}
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: `${LABEL_COL}px repeat(${columns.length}, ${COL}px)`,
                alignItems: 'stretch',
                width: gridWidth
              }}
            >
              {/* The audiences. */}
              <Box sx={{ ...cellSx('label'), position: 'sticky', top: 0, zIndex: 2, background: SURFACE }} />
              {columns.map((c) => (
                <Box
                  key={c.audience.id}
                  sx={{ ...cellSx(c), position: 'sticky', top: 0, zIndex: 2, background: SURFACE, textAlign: 'center' }}
                >
                  <DosenTooltip title={<AudienceTip audience={c.audience} />}>
                    <Typography
                      noWrap
                      sx={{ fontFamily: FONT, fontSize: 13, fontWeight: 600, color: TEXT, maxWidth: '100%' }}
                    >
                      {c.audience.name}
                    </Typography>
                  </DosenTooltip>
                </Box>
              ))}

              {modules.map((m, mi) => (
                <React.Fragment key={m.id}>
                  {/* The module: a heading across the grid, a thick line above it. */}
                  <Box
                    sx={{
                      gridColumn: '1 / -1',
                      borderTop: THICK,
                      borderBottom: `1px solid ${LINE}`,
                      py: 1,
                      display: 'flex',
                      alignItems: 'center'
                    }}
                  >
                    <Typography noWrap sx={{ fontFamily: FONT, fontSize: 13, fontWeight: 600, color: TEXT }}>
                      {mi + 1}. {m.title}
                    </Typography>
                  </Box>
                  {m.lessons.map((l, li) => (
                    <React.Fragment key={l.id}>
                      <Box sx={cellSx('label')}>
                        <Typography noWrap sx={{ fontFamily: FONT, fontSize: 13, color: TEXT_2, minWidth: 0 }}>
                          {mi + 1}.{li + 1} {l.title}
                        </Typography>
                      </Box>
                      {columns.map((c) => (
                        <Box key={c.audience.id} sx={cellSx(c)}>
                          {l.cells[c.audience.id] && (
                            <AccessText audienceName={c.audience.name} access={l.cells[c.audience.id]} />
                          )}
                        </Box>
                      ))}
                    </React.Fragment>
                  ))}
                </React.Fragment>
              ))}
              <Box sx={{ gridColumn: '1 / -1', borderTop: THICK }} />
            </Box>
          </>
        )}
      </Box>
    </Dialog>
  );
};

export default RequirementsPanel;
